import { create } from 'zustand';
import { getActiveHouseholdPin } from '@/lib/active-household-pin';
import type { CaregiverRole } from '@/types';

/**
 * The active household and the caller's access level IN it.
 *
 * These two travel together, always. A user's access level is PER household
 * — owner of theirs, viewer in another — so a role that outlives the
 * household it was read from is a privilege bug waiting to happen. Keeping
 * them in one store makes it impossible to update one without the other.
 *
 * Not persisted. The `yaruta_active_household` pin (owned by
 * household.service) is already the durable copy of the id, and a second
 * persisted copy would be a second source of truth to keep in sync. The role
 * is deliberately never persisted: it is a server-side fact that can change
 * between sessions (a demotion, a transfer), so it must be re-read, never
 * restored.
 */
interface HouseholdStoreState {
  /**
   * Seeded synchronously from the pin so the first render already has an id
   * to query with. Until `resolved` is true this is a HINT — RLS is what
   * actually gates the data, so an out-of-date pin returns nothing rather
   * than leaking another household's rows.
   */
  householdId: string | null;
  /** Null until resolved — never assume a level before the server says so. */
  role: CaregiverRole | null;
  /** True once a real resolution has landed (even one that found nothing). */
  resolved: boolean;
}

interface HouseholdStoreActions {
  /** Record an authoritative resolution: household and role, together. */
  setActiveHousehold: (
    householdId: string | null,
    role: CaregiverRole | null,
  ) => void;
  /**
   * Point at a different household whose role is not yet known — used by the
   * switcher, which changes households before it can re-read the level.
   * Drops `role` to null and `resolved` to false so no consumer can carry the
   * previous household's permissions across the switch.
   */
  beginSwitch: (householdId: string) => void;
  clear: () => void;
}

export const useHouseholdStore = create<
  HouseholdStoreState & HouseholdStoreActions
>()((set) => ({
  householdId: getActiveHouseholdPin(),
  role: null,
  resolved: false,

  setActiveHousehold: (householdId, role) =>
    set({ householdId, role, resolved: true }),

  beginSwitch: (householdId) =>
    set({ householdId, role: null, resolved: false }),

  clear: () => set({ householdId: null, role: null, resolved: false }),
}));
